import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, BarChart3, FileText, HeartHandshake, MessageSquare, Plus, Trash2, RefreshCw, Save, Check, ExternalLink, Lock, Activity } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useHistoryTab } from "@/hooks/useHistoryTab";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Markdown } from "@/components/Markdown";
import { formatDistanceToNowStrict } from "date-fns";
import { toast } from "sonner";

export default function AdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useHistoryTab("analytics");
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-10 py-20 text-center" data-testid="admin-forbidden">
          <Lock className="h-12 w-12 mx-auto text-muted-foreground" strokeWidth={1.25} />
          <h1 className="font-heading text-3xl mt-4">Admin only</h1>
          <p className="text-sm text-muted-foreground mt-2">This area is restricted to site operators.</p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="admin-page">
      <Navbar />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-10 py-10 w-full">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-xl brand-bg text-black flex items-center justify-center"><Shield className="h-5 w-5" strokeWidth={1.75} /></span>
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-muted-foreground">/ admin</div>
            <h1 className="font-heading text-3xl sm:text-4xl">Owner Dashboard</h1>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2 max-w-2xl">
          Aggregate stats only — no personal data, names, emails, or transaction details are stored or shown here.
        </p>

        <Tabs value={tab} onValueChange={setTab} className="mt-8">
          <TabsList className="bg-foreground/5 border border-border w-full grid grid-cols-2 sm:grid-cols-4 h-auto" data-testid="admin-tabs">
            <TabsTrigger value="analytics" data-testid="admin-tab-analytics"><BarChart3 className="h-4 w-4 mr-1.5" strokeWidth={1.5} />Analytics</TabsTrigger>
            <TabsTrigger value="content" data-testid="admin-tab-content"><FileText className="h-4 w-4 mr-1.5" strokeWidth={1.5} />Content</TabsTrigger>
            <TabsTrigger value="sponsors" data-testid="admin-tab-sponsors"><HeartHandshake className="h-4 w-4 mr-1.5" strokeWidth={1.5} />Sponsors</TabsTrigger>
            <TabsTrigger value="feedback" data-testid="admin-tab-feedback"><MessageSquare className="h-4 w-4 mr-1.5" strokeWidth={1.5} />Feedback</TabsTrigger>
          </TabsList>

          <TabsContent value="analytics" className="mt-6"><AnalyticsTab /></TabsContent>
          <TabsContent value="content" className="mt-6"><ContentTab /></TabsContent>
          <TabsContent value="sponsors" className="mt-6"><SponsorsTab /></TabsContent>
          <TabsContent value="feedback" className="mt-6"><FeedbackTab /></TabsContent>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
}

// ==================== Analytics ====================
const AnalyticsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/analytics");
      setData(data);
    } catch (e) { toast.error("Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
          Generated · {formatDistanceToNowStrict(new Date(data.generated_at), { addSuffix: true })}
        </div>
        <Button onClick={load} variant="outline" className="border-border h-10" data-testid="analytics-refresh">
          <RefreshCw className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Refresh
        </Button>
      </div>

      <Section title="Users">
        <StatGrid stats={[
          { label: "Total users", value: data.users.total, testId: "stat-users-total" },
          { label: "PRO users", value: data.users.pro, brand: true },
          { label: "Admins", value: data.users.admin },
          { label: "Signups · 7d", value: data.users.signups_7d, accent: true },
          { label: "Signups · 30d", value: data.users.signups_30d },
        ]} />
      </Section>

      <Section title="Activity">
        <StatGrid stats={[
          { label: "Groups", value: data.activity.groups },
          { label: "Bills", value: data.activity.bills },
          { label: "Settlements", value: data.activity.settlements },
          { label: "Direct splits", value: data.activity.direct_splits },
          { label: "Direct settlements", value: data.activity.direct_settlements },
          { label: "Bills · 7d", value: data.activity.bills_7d, accent: true },
          { label: "Bills · 30d", value: data.activity.bills_30d },
          { label: "Groups · 7d", value: data.activity.groups_7d, accent: true },
          { label: "Settlements · 7d", value: data.activity.settlements_7d, accent: true },
        ]} />
      </Section>

      <Section title="PRO modules">
        <StatGrid stats={[
          { label: "Incomes", value: data.pro_modules.incomes },
          { label: "Expenses", value: data.pro_modules.expenses },
          { label: "Savings goals", value: data.pro_modules.savings_goals },
          { label: "Recurring", value: data.pro_modules.recurring },
          { label: "Templates", value: data.pro_modules.templates },
        ]} />
      </Section>

      <Section title="Feedback inbox">
        <StatGrid stats={[
          { label: "New", value: data.feedback.new, accent: data.feedback.new > 0 },
          { label: "Seen", value: data.feedback.seen },
          { label: "Closed", value: data.feedback.closed },
          { label: "Active sponsors", value: data.sponsors_active, brand: true },
        ]} />
      </Section>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <DistroCard title="Languages" map={data.languages} testId="distro-lang" />
        <DistroCard title="Currencies" map={data.currencies} testId="distro-currency" />
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <section>
    <div className="text-xs font-mono uppercase tracking-[0.3em] text-muted-foreground mb-3">{title}</div>
    {children}
  </section>
);

const StatGrid = ({ stats }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
    {stats.map((s, i) => (
      <motion.div
        key={s.label} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
        className={`rounded-xl border p-4 bg-card/40 ${s.brand ? "border-[color:var(--brand)]/40" : s.accent ? "border-[color:var(--brand)]/30" : "border-border"}`}
        data-testid={s.testId || `stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground truncate">{s.label}</div>
        <div className={`mt-2 font-mono tabular-nums text-2xl ${s.brand || s.accent ? "brand-text" : ""}`}>{s.value}</div>
      </motion.div>
    ))}
  </div>
);

const DistroCard = ({ title, map, testId }) => {
  const entries = Object.entries(map || {}).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
  return (
    <div className="rounded-2xl border border-border bg-card/40 p-5" data-testid={testId}>
      <div className="text-xs font-mono uppercase tracking-[0.3em] text-muted-foreground mb-3">{title}</div>
      {entries.length === 0 ? (
        <div className="text-sm text-muted-foreground">No data</div>
      ) : (
        <div className="space-y-2">
          {entries.map(([k, v]) => {
            const pct = Math.round((v / total) * 100);
            return (
              <div key={k}>
                <div className="flex items-center justify-between text-xs font-mono uppercase tracking-wider">
                  <span>{k}</span>
                  <span className="text-muted-foreground">{v} · {pct}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-foreground/5 mt-1 overflow-hidden">
                  <div className="h-full brand-bg" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ==================== Traffic (self-hosted analytics beacon) ====================
const TrafficTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(14);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/traffic?days=${range}`);
      setData(data);
    } catch (e) { toast.error("Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [range]);

  if (loading || !data) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const maxDaily = Math.max(1, ...data.daily.map((d) => d.views));
  const maxUniq = Math.max(1, ...data.daily.map((d) => d.unique_visitors));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
          Range · last {range} days
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {[7, 14, 30, 60].map((d) => (
            <button key={d} onClick={() => setRange(d)}
              className={`px-3 h-9 rounded-md text-xs font-mono uppercase tracking-wider transition ${range === d ? "bg-foreground/10 text-foreground" : "text-muted-foreground hover:text-foreground border border-border"}`}
              data-testid={`traffic-range-${d}`}
            >{d}d</button>
          ))}
          <Button onClick={load} variant="outline" className="border-border h-9" data-testid="traffic-refresh">
            <RefreshCw className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Refresh
          </Button>
        </div>
      </div>

      <StatGrid stats={[
        { label: "Views today", value: data.totals.views_today, accent: true, testId: "traffic-views-today" },
        { label: "Unique today", value: data.totals.unique_today, brand: true },
        { label: `Views · ${range}d`, value: data.totals.views_range },
        { label: "Top page views", value: data.top_pages[0]?.views || 0 },
      ]} />

      {/* Daily chart — dual line, pure CSS bars */}
      <section className="rounded-2xl border border-border bg-card/40 p-5" data-testid="traffic-daily">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-muted-foreground">Daily traffic</div>
          <span className="text-[10px] font-mono uppercase tracking-wider brand-text">▮ views</span>
          <span className="text-[10px] font-mono uppercase tracking-wider text-amber-400">▮ unique</span>
        </div>
        {data.daily.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center">No traffic yet — visit some pages to seed the chart.</div>
        ) : (
          <div className="flex items-end gap-1.5 h-40">
            {data.daily.map((d) => (
              <div key={d.day} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${d.day} · ${d.views} views · ${d.unique_visitors} unique`}>
                <div className="w-full flex items-end gap-0.5 h-32">
                  <div className="flex-1 rounded-t brand-bg" style={{ height: `${(d.views / maxDaily) * 100}%`, minHeight: d.views > 0 ? 2 : 0 }} />
                  <div className="flex-1 rounded-t bg-amber-400" style={{ height: `${(d.unique_visitors / maxUniq) * 100}%`, minHeight: d.unique_visitors > 0 ? 2 : 0 }} />
                </div>
                <div className="text-[9px] font-mono text-muted-foreground tracking-wider truncate w-full text-center">{d.day.slice(5)}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ListCard title="Top pages" empty="No page views recorded" items={data.top_pages.map((p) => ({ label: p.path, value: p.views }))} testId="traffic-top-pages" />
        <ListCard title="Referrers" empty="No external referrers" items={data.referrers.map((r) => ({ label: r.host, value: r.n }))} testId="traffic-referrers" />
        <DistroCard title="Devices" map={Object.fromEntries(data.devices.map((d) => [d.device, d.n]))} testId="traffic-devices" />
        <DistroCard title="Viewports" map={Object.fromEntries(data.viewports.map((v) => [v.vw, v.n]))} testId="traffic-viewports" />
      </div>

      <ListCard title="Browser languages" empty="No language data yet" items={data.languages.map((l) => ({ label: l.lang || "unknown", value: l.n }))} testId="traffic-languages" />

      <div className="rounded-xl border border-border bg-foreground/[0.02] p-4 text-[11px] text-muted-foreground leading-relaxed" data-testid="traffic-privacy-note">
        <strong className="text-foreground">Privacy note.</strong> This is a first-party beacon. No cookies, no third-party requests, no raw IPs stored.
        We hash (IP + user-agent + daily-rotating salt) into a 16-char token that lets us count unique visitors for the current day only.
        After UTC midnight the salt rotates and yesterday&apos;s visitors cannot be reconnected to today&apos;s traffic. Zero personal data ever leaves your database.
      </div>
    </div>
  );
};

const ListCard = ({ title, items, empty, testId }) => (
  <div className="rounded-2xl border border-border bg-card/40 p-5" data-testid={testId}>
    <div className="text-xs font-mono uppercase tracking-[0.3em] text-muted-foreground mb-3">{title}</div>
    {items.length === 0 ? (
      <div className="text-sm text-muted-foreground">{empty}</div>
    ) : (
      <div className="space-y-1.5">
        {items.map((it, i) => {
          const total = items.reduce((s, x) => s + x.value, 0) || 1;
          const pct = Math.round((it.value / total) * 100);
          return (
            <div key={`${it.label}-${i}`}>
              <div className="flex items-center justify-between text-xs font-mono">
                <span className="truncate max-w-[70%]">{it.label}</span>
                <span className="text-muted-foreground shrink-0">{it.value} · {pct}%</span>
              </div>
              <div className="h-1 rounded-full bg-foreground/5 mt-1 overflow-hidden">
                <div className="h-full brand-bg" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);


// ==================== Content (Impressum + Privacy editor) ====================
const ContentTab = () => {
  const [slug, setSlug] = useState("impressum");
  const [body, setBody] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async (s) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/site/content/${s}`);
      setBody(data.body || "");
      setUpdatedAt(data.updated_at);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(slug); }, [slug]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/site/content/${slug}`, { body });
      setUpdatedAt(data.updated_at);
      toast.success("Saved · live everywhere");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={slug} onValueChange={setSlug}>
          <SelectTrigger className="bg-muted border-border h-10 w-48" data-testid="content-slug-picker"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="impressum">Impressum</SelectItem>
            <SelectItem value="privacy">Privacy Policy</SelectItem>
          </SelectContent>
        </Select>
        {updatedAt && <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Updated {formatDistanceToNowStrict(new Date(updatedAt), { addSuffix: true })}</span>}
        <Button onClick={save} disabled={saving || loading} className="brand-bg text-black hover:opacity-90 h-10 ml-auto" data-testid="content-save">
          <Save className="h-4 w-4 mr-1.5" strokeWidth={1.5} />{saving ? "Saving…" : "Save"}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Markdown</Label>
          <Textarea
            rows={22}
            value={body} onChange={(e) => setBody(e.target.value)}
            className="mt-1.5 bg-muted border-border font-mono text-sm leading-relaxed"
            data-testid="content-editor"
            placeholder="# Heading…"
          />
          <p className="text-[11px] text-muted-foreground mt-2"># Heading · **bold** · *italic* · `code` · - list · --- divider</p>
        </div>
        <div>
          <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Live preview</Label>
          <div className="mt-1.5 rounded-xl border border-border bg-card/40 p-5 min-h-[400px]" data-testid="content-preview">
            <Markdown source={body} />
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== Sponsors CRUD ====================
const TIERS = ["platinum", "gold", "silver", "supporter"];

const SponsorsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { name: "", url: "", logo_emoji: "✨", tier: "supporter", note: "", order: 0, active: true };
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/sponsors");
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...empty, ...s }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    try {
      if (editing) {
        await api.patch(`/admin/sponsors/${editing.id}`, form);
        toast.success("Updated");
      } else {
        await api.post("/admin/sponsors", form);
        toast.success("Added");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (s) => {
    if (!window.confirm(`Remove ${s.name}?`)) return;
    try { await api.delete(`/admin/sponsors/${s.id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggle = async (s) => {
    try { await api.patch(`/admin/sponsors/${s.id}`, { active: !s.active }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">{items.length} entries</div>
        <Button onClick={openNew} className="brand-bg text-black hover:opacity-90 h-10" data-testid="sponsors-new-btn">
          <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Add sponsor
        </Button>
      </div>

      {loading ? <div className="text-sm text-muted-foreground">Loading…</div> :
        items.length === 0 ? <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="sponsors-empty">No sponsors yet — add your first.</div> :
        <div className="rounded-2xl border border-border bg-card/40 divide-y divide-border" data-testid="sponsors-list">
          {items.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`sponsor-row-${s.id}`}>
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <span className="h-10 w-10 rounded-lg bg-foreground/[0.04] flex items-center justify-center text-xl shrink-0">{s.logo_emoji}</span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium truncate">{s.name}</span>
                    <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-foreground/5 text-muted-foreground">{s.tier}</span>
                    {!s.active && <span className="text-[10px] font-mono uppercase tracking-wider text-red-400">hidden</span>}
                  </div>
                  {s.url && <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-[11px] font-mono text-muted-foreground hover:brand-text truncate flex items-center gap-1"><ExternalLink className="h-3 w-3" strokeWidth={1.5} />{s.url}</a>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" variant="outline" onClick={() => toggle(s)} className="border-border h-9" data-testid={`sponsor-toggle-${s.id}`}>{s.active ? "Hide" : "Show"}</Button>
                <Button size="sm" variant="outline" onClick={() => openEdit(s)} className="border-border h-9" data-testid={`sponsor-edit-${s.id}`}>Edit</Button>
                <Button size="sm" variant="outline" onClick={() => del(s)} className="border-red-500/40 text-red-400 hover:bg-red-500/10 h-9 w-9 p-0" data-testid={`sponsor-del-${s.id}`} aria-label="Delete">
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      }

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="sponsor-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editing ? "Edit sponsor" : "New sponsor"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-muted border-border h-11" placeholder="ACME Inc." data-testid="sponsor-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Logo emoji</Label>
                <Input value={form.logo_emoji} onChange={(e) => setForm({ ...form, logo_emoji: e.target.value })} className="mt-1.5 bg-muted border-border h-11 text-xl" maxLength={2} data-testid="sponsor-emoji" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Tier</Label>
                <Select value={form.tier} onValueChange={(v) => setForm({ ...form, tier: v })}>
                  <SelectTrigger className="mt-1.5 bg-muted border-border h-11" data-testid="sponsor-tier"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {TIERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">URL</Label>
              <Input value={form.url || ""} onChange={(e) => setForm({ ...form, url: e.target.value })} className="mt-1.5 bg-muted border-border h-11" placeholder="https://…" data-testid="sponsor-url" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Note</Label>
              <Textarea rows={2} value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5 bg-muted border-border" placeholder="Optional short description" data-testid="sponsor-note" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Order</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) || 0 })} className="mt-1.5 bg-muted border-border h-11 font-mono" data-testid="sponsor-order" />
              </div>
              <label className="flex items-end gap-2 pb-1.5">
                <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 accent-[var(--brand)]" data-testid="sponsor-active" />
                <span className="text-sm">Visible publicly</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} className="brand-bg text-black hover:opacity-90 w-full h-11" data-testid="sponsor-save">{editing ? "Save changes" : "Add sponsor"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== Feedback inbox ====================
const FeedbackTab = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const q = filter === "all" ? "" : `?status=${filter}`;
      const { data } = await api.get(`/admin/feedback${q}`);
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const setStatus = async (f, status) => {
    try {
      await api.patch(`/admin/feedback/${f.id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error("Failed"); }
  };
  const del = async (f) => {
    if (!window.confirm("Delete this feedback?")) return;
    try { await api.delete(`/admin/feedback/${f.id}`); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const tone = (cat) => ({
    bug: "text-red-400 border-red-500/30 bg-red-500/5",
    feature: "brand-text border-[color:var(--brand)]/30",
    praise: "text-amber-400 border-amber-500/30",
    general: "text-muted-foreground border-border",
  }[cat] || "text-muted-foreground border-border");

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        {["all", "new", "seen", "closed"].map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 h-9 rounded-md text-xs font-mono uppercase tracking-wider transition ${filter === s ? "bg-foreground/10 text-foreground" : "text-muted-foreground hover:text-foreground border border-border"}`}
            data-testid={`feedback-filter-${s}`}
          >{s}</button>
        ))}
      </div>

      {loading ? <div className="text-sm text-muted-foreground">Loading…</div> :
        items.length === 0 ? <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="feedback-empty">No feedback in this view.</div> :
        <div className="space-y-3" data-testid="feedback-list">
          {items.map((f) => (
            <div key={f.id} className="rounded-xl border border-border bg-card/40 p-4" data-testid={`feedback-row-${f.id}`}>
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${tone(f.category)}`}>{f.category}</span>
                <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{formatDistanceToNowStrict(new Date(f.created_at), { addSuffix: true })}</span>
                <span className={`text-[10px] font-mono uppercase tracking-wider ml-auto ${f.status === "new" ? "brand-text" : "text-muted-foreground"}`}>{f.status}</span>
              </div>
              <p className="text-sm whitespace-pre-wrap break-words">{f.message}</p>
              {f.contact && <p className="text-xs font-mono text-muted-foreground mt-2">Reply to · {f.contact}</p>}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {f.status !== "seen" && <Button size="sm" variant="outline" onClick={() => setStatus(f, "seen")} className="border-border h-9" data-testid={`fb-mark-seen-${f.id}`}><Check className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />Seen</Button>}
                {f.status !== "closed" && <Button size="sm" variant="outline" onClick={() => setStatus(f, "closed")} className="border-border h-9" data-testid={`fb-mark-closed-${f.id}`}>Close</Button>}
                {f.status !== "new" && <Button size="sm" variant="outline" onClick={() => setStatus(f, "new")} className="border-border h-9" data-testid={`fb-mark-new-${f.id}`}>Reopen</Button>}
                <Button size="sm" variant="outline" onClick={() => del(f)} className="border-red-500/40 text-red-400 hover:bg-red-500/10 h-9 ml-auto" data-testid={`fb-del-${f.id}`}><Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} /></Button>
              </div>
            </div>
          ))}
        </div>
      }
    </div>
  );
};
